import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SpaBadge } from '@/components/ui/spa-badge';
import { TypeBadge } from '@/components/ui/type-badge';
import { SectorBadge } from '@/components/ui/sector-badge';
import { MapPin, Clock, ArrowLeft, ExternalLink, Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { z } from 'zod';

interface EventDetail {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  city: string;
  country: string;
  venue_name: string;
  venue_address: string;
  description: string | null;
  registration_link: string | null;
  focus_tags: string[];
  status: string | null;
}

interface Opportunity {
  id: string;
  title: string;
  type: 'job' | 'tender' | 'jv' | 'training';
  sector: 'mining' | 'processing' | 'infrastructure' | 'industrial' | 'services' | 'other';
  country: string;
  city: string | null;
  remote: boolean;
  spa_linked: boolean;
  spa_pillar: 'critical_minerals' | 'industrialization' | 'infrastructure' | 'governance' | null;
  deadline: string | null;
  description: string;
  created_at: string;
  company: { name: string; logo_url: string | null };
}

const FOCUS_TAG_COLORS: Record<string, string> = {
  diaspora: 'bg-secondary/10 text-secondary border-secondary/20',
  investors: 'bg-primary/10 text-primary border-primary/20',
  youth: 'bg-spa-minerals/10 text-spa-minerals border-spa-minerals/20',
  academic: 'bg-spa-governance/10 text-spa-governance border-spa-governance/20',
  government: 'bg-foreground/10 text-foreground border-foreground/20',
  civil_society: 'bg-spa-infrastructure/10 text-spa-infrastructure border-spa-infrastructure/20',
};

function FocusTag({ tag }: { tag: string }) {
  const colorClass = FOCUS_TAG_COLORS[tag] || 'bg-muted text-muted-foreground';
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium capitalize ${colorClass}`}>
      {tag.replace(/_/g, ' ')}
    </span>
  );
}

function formatTime(time: string) {
  return time.slice(0, 5);
}

const registrationSchema = z.object({
  fullName: z.string().trim().min(1, 'Required').max(100),
  email: z.string().trim().email('Invalid email').max(255),
  organization: z.string().trim().max(200).optional(),
  role: z.string().min(1, 'Required'),
  country: z.string().trim().max(100).optional(),
});

type RegistrationForm = z.infer<typeof registrationSchema>;

const ROLES = ['Company Representative', 'Professional', 'Government Official', 'Academic', 'Other'] as const;

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);

  // Registration form state
  const [form, setForm] = useState<RegistrationForm>({ fullName: '', email: '', organization: '', role: '', country: '' });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (id) fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [eventRes, oppsRes] = await Promise.all([
        supabase.from('events').select('*').eq('id', id).single(),
        supabase.from('opportunities').select('*, company:companies(name, logo_url)').eq('status', 'open').order('created_at', { ascending: false }).limit(3),
      ]);
      if (eventRes.error) throw eventRes.error;
      const e = eventRes.data as any;
      setEvent({ ...e, focus_tags: Array.isArray(e.focus_tags) ? e.focus_tags : [] });
      setOpportunities((oppsRes.data as unknown as Opportunity[]) || []);
    } catch (error) {
      console.error('Error fetching event:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const result = registrationSchema.safeParse(form);
    if (!result.success) {
      const errors: Record<string, string> = {};
      result.error.issues.forEach(issue => {
        errors[issue.path[0] as string] = issue.message;
      });
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setSubmitted(true);
    toast.success(t('events.registrationSuccess'));
    setForm({ fullName: '', email: '', organization: '', role: '', country: '' });
    setTimeout(() => setSubmitted(false), 3000);
  };

  if (loading) {
    return (
      <Layout>
        <div className="gradient-hero py-16">
          <div className="container space-y-4">
            <Skeleton className="h-10 w-2/3 bg-white/20" />
            <Skeleton className="h-6 w-1/2 bg-white/20" />
            <Skeleton className="h-5 w-1/3 bg-white/20" />
          </div>
        </div>
        <div className="container py-8">
          <div className="grid gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!event) {
    return (
      <Layout>
        <div className="container py-12 text-center">
          <CalendarIcon className="mx-auto mb-4 h-16 w-16 text-muted-foreground/40" />
          <h1 className="text-2xl font-bold">Event not found</h1>
          <Button asChild variant="outline" className="mt-4">
            <Link to="/events">{t('common.back')}</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const eventDate = new Date(event.date);
  const formattedDate = format(eventDate, 'EEEE, MMMM d, yyyy');

  return (
    <Layout>
      {/* Hero */}
      <div className="gradient-hero py-16">
        <div className="container">
          <Button
            variant="ghost"
            className="mb-4 gap-2 text-primary-foreground/80 hover:text-primary-foreground hover:bg-white/10"
            onClick={() => navigate('/events')}
          >
            <ArrowLeft className="h-4 w-4" />
            ← {t('events.title')}
          </Button>

          <h1 className="text-3xl font-bold text-primary-foreground md:text-4xl">{event.title}</h1>

          <div className="mt-4 flex flex-wrap items-center gap-4 text-primary-foreground/90">
            <span className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              {formattedDate} · {formatTime(event.start_time)} – {formatTime(event.end_time)}
            </span>
            <span className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              {event.city}, {event.country}
            </span>
          </div>

          {event.focus_tags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {event.focus_tags.map(tag => (
                <span
                  key={tag}
                  className="inline-flex items-center rounded-full bg-white/20 px-2.5 py-1 text-xs font-medium capitalize text-primary-foreground"
                >
                  {tag.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="container py-8">
        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main */}
          <div className="lg:col-span-2 space-y-6">
            {/* Venue */}
            <Card>
              <CardContent className="flex items-start gap-4 p-6">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-semibold">{t('events.venue')}</h3>
                  <p className="text-sm font-medium">{event.venue_name}</p>
                  <p className="text-sm text-muted-foreground">{event.venue_address}</p>
                </div>
              </CardContent>
            </Card>

            {/* Description */}
            <Card>
              <CardContent className="p-6">
                <div className="whitespace-pre-wrap text-sm">
                  {event.description || 'Details for this event will be announced soon.'}
                </div>
              </CardContent>
            </Card>

            {/* Focus Tags */}
            {event.focus_tags.length > 0 && (
              <div>
                <h3 className="mb-3 font-semibold">Focus Areas</h3>
                <div className="flex flex-wrap gap-2">
                  {event.focus_tags.map(tag => (
                    <FocusTag key={tag} tag={tag} />
                  ))}
                </div>
              </div>
            )}

            {/* Related Opportunities */}
            <div>
              <h2 className="mb-4 text-xl font-semibold">{t('events.relatedOpportunities')}</h2>
              {opportunities.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('opportunities.directory.noResults')}</p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {opportunities.map(opp => (
                    <Card key={opp.id} className={`transition-shadow hover:shadow-md ${opp.spa_linked ? 'spa-card-highlight' : ''}`}>
                      <CardContent className="p-4">
                        <div className="mb-2 flex items-start justify-between gap-2">
                          <TypeBadge type={opp.type} />
                          {opp.spa_linked && <SpaBadge pillar={opp.spa_pillar || undefined} size="sm" />}
                        </div>
                        <h3 className="font-semibold line-clamp-2 text-sm">{opp.title}</h3>
                        <p className="mt-1 text-xs text-muted-foreground">{opp.company.name}</p>
                        <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {opp.city ? `${opp.city}, ` : ''}{opp.country}
                        </p>
                        <SectorBadge sector={opp.sector} showIcon={false} className="mt-2" />
                      </CardContent>
                      <CardFooter className="border-t p-3">
                        <Button asChild variant="outline" size="sm" className="w-full">
                          <Link to={`/opportunities/${opp.id}`}>{t('opportunities.directory.viewDetails')}</Link>
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              )}
              <Button asChild variant="link" className="mt-3 px-0">
                <Link to="/opportunities">{t('nav.opportunities')} →</Link>
              </Button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-lg">
                  {event.registration_link ? t('events.register') : t('events.registrationForm')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {event.registration_link ? (
                  <Button asChild className="w-full gap-2" size="lg">
                    <a href={event.registration_link} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4" />
                      {t('events.register')}
                    </a>
                  </Button>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="fullName">Full Name *</Label>
                      <Input
                        id="fullName"
                        value={form.fullName}
                        onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
                        maxLength={100}
                      />
                      {formErrors.fullName && <p className="text-xs text-destructive">{formErrors.fullName}</p>}
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                        maxLength={255}
                      />
                      {formErrors.email && <p className="text-xs text-destructive">{formErrors.email}</p>}
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="organization">Organization / Affiliation</Label>
                      <Input
                        id="organization"
                        value={form.organization}
                        onChange={e => setForm(f => ({ ...f, organization: e.target.value }))}
                        maxLength={200}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="role">Role *</Label>
                      <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select your role" />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map(r => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formErrors.role && <p className="text-xs text-destructive">{formErrors.role}</p>}
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        value={form.country}
                        onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
                        maxLength={100}
                      />
                    </div>

                    <Button type="submit" className="w-full" size="lg">
                      {t('common.submit')} Registration
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
